/**
 * Achievements service layer.
 * Manages achievement definitions, checking/awarding achievements,
 * and retrieving unlocked achievements for users.
 */
import {
  collection,
  doc,
  setDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const USERS_COLLECTION = 'users';
const ACHIEVEMENTS_SUBCOLLECTION = 'achievements';

/**
 * Static achievement definitions.
 * Each achievement has a unique key, display info, and unlock criteria.
 */
const ACHIEVEMENT_DEFINITIONS = [
  // First Workout
  {
    type: 'first_workout',
    tier: 'bronze',
    name: 'First Workout',
    description: 'Log your first workout',
    icon: 'fitness_center',
    category: 'workout',
    criteria: { event: 'workout_logged', threshold: 1 },
  },

  // Week Warrior tiers
  {
    type: 'week_warrior_bronze',
    tier: 'bronze',
    name: 'Week Warrior',
    description: '7-day workout streak',
    icon: 'local_fire_department',
    category: 'streak',
    criteria: { event: 'streak_updated', threshold: 7 },
  },
  {
    type: 'week_warrior_silver',
    tier: 'silver',
    name: 'Week Warrior',
    description: '30-day workout streak',
    icon: 'local_fire_department',
    category: 'streak',
    criteria: { event: 'streak_updated', threshold: 30 },
  },
  {
    type: 'week_warrior_gold',
    tier: 'gold',
    name: 'Week Warrior',
    description: '100-day workout streak',
    icon: 'local_fire_department',
    category: 'streak',
    criteria: { event: 'streak_updated', threshold: 100 },
  },

  // Century Club
  {
    type: 'century_club',
    tier: 'gold',
    name: 'Century Club',
    description: 'Log 100 workouts',
    icon: 'emoji_events',
    category: 'workout',
    criteria: { event: 'workout_logged', threshold: 100 },
  },

  // Social Butterfly tiers
  {
    type: 'social_butterfly_bronze',
    tier: 'bronze',
    name: 'Social Butterfly',
    description: 'Get 10 followers',
    icon: 'people',
    category: 'social',
    criteria: { event: 'follower_gained', threshold: 10 },
  },
  {
    type: 'social_butterfly_silver',
    tier: 'silver',
    name: 'Social Butterfly',
    description: 'Get 50 followers',
    icon: 'people',
    category: 'social',
    criteria: { event: 'follower_gained', threshold: 50 },
  },
  {
    type: 'social_butterfly_gold',
    tier: 'gold',
    name: 'Social Butterfly',
    description: 'Get 100 followers',
    icon: 'people',
    category: 'social',
    criteria: { event: 'follower_gained', threshold: 100 },
  },

  // Meal Prepper
  {
    type: 'meal_prepper',
    tier: 'bronze',
    name: 'Meal Prepper',
    description: 'Log 7 consecutive days of meals',
    icon: 'restaurant',
    category: 'nutrition',
    criteria: { event: 'meal_streak', threshold: 7 },
  },

  // Transformation
  {
    type: 'transformation',
    tier: 'bronze',
    name: 'Transformation',
    description: 'Post your first progress photo',
    icon: 'photo_camera',
    category: 'progress',
    criteria: { event: 'progress_photo_posted', threshold: 1 },
  },

  // Goal Getter tiers
  {
    type: 'goal_getter_bronze',
    tier: 'bronze',
    name: 'Goal Getter',
    description: 'Complete a goal',
    icon: 'flag',
    category: 'goals',
    criteria: { event: 'goal_completed', threshold: 1 },
  },
  {
    type: 'goal_getter_silver',
    tier: 'silver',
    name: 'Goal Getter',
    description: 'Complete 5 goals',
    icon: 'flag',
    category: 'goals',
    criteria: { event: 'goal_completed', threshold: 5 },
  },
  {
    type: 'goal_getter_gold',
    tier: 'gold',
    name: 'Goal Getter',
    description: 'Complete 10 goals',
    icon: 'flag',
    category: 'goals',
    criteria: { event: 'goal_completed', threshold: 10 },
  },

  // Accountability Ace tiers
  {
    type: 'accountability_ace_bronze',
    tier: 'bronze',
    name: 'Accountability Ace',
    description: '7-day check-in streak with partner',
    icon: 'handshake',
    category: 'accountability',
    criteria: { event: 'checkin_streak', threshold: 7 },
  },
  {
    type: 'accountability_ace_silver',
    tier: 'silver',
    name: 'Accountability Ace',
    description: '30-day check-in streak with partner',
    icon: 'handshake',
    category: 'accountability',
    criteria: { event: 'checkin_streak', threshold: 30 },
  },

  // Consistent Poster
  {
    type: 'first_post',
    tier: 'bronze',
    name: 'First Post',
    description: 'Create your first post',
    icon: 'edit',
    category: 'social',
    criteria: { event: 'post_created', threshold: 1 },
  },
];

/**
 * Serialize an achievement document snapshot.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {Object}
 */
const serializeAchievement = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    type: data.type || '',
    tier: data.tier || 'bronze',
    unlockedAt: data.unlockedAt instanceof Timestamp
      ? data.unlockedAt.toDate().toISOString()
      : data.unlockedAt || new Date().toISOString(),
    seen: data.seen || false,
  };
};

/**
 * Get all possible achievement definitions with unlock criteria.
 * @returns {Array} Array of achievement definition objects.
 */
export const getAchievementDefinitions = () => {
  return [...ACHIEVEMENT_DEFINITIONS];
};

/**
 * Get all unlocked achievements for a user.
 * @param {string} userId - The user's ID.
 * @returns {Promise<Array>} Array of unlocked achievement objects.
 */
export const getAchievements = async (userId) => {
  try {
    const achievementsRef = collection(
      db,
      USERS_COLLECTION,
      userId,
      ACHIEVEMENTS_SUBCOLLECTION
    );
    const querySnapshot = await getDocs(achievementsRef);
    return querySnapshot.docs.map(serializeAchievement);
  } catch (error) {
    handleServiceError(error, 'Get achievements', 'Failed to load achievements. Please try again.');
  }
};

/**
 * Check if a user has earned new achievements after performing an action.
 * Compares the event against all achievement criteria and awards any that are newly met.
 *
 * @param {string} userId - The user's ID.
 * @param {string} eventType - The type of event that occurred.
 * @param {Object} eventData - Additional data about the event.
 * @param {number} [eventData.count] - The cumulative count for the event (e.g., total workouts).
 * @param {number} [eventData.streak] - The current streak value.
 * @returns {Promise<Array>} Array of newly awarded achievement objects.
 */
export const checkAndAwardAchievements = async (userId, eventType, eventData = {}) => {
  try {
    // Get already unlocked achievements
    const existingAchievements = await getAchievements(userId);
    const existingTypes = new Set(existingAchievements.map((a) => a.type));

    // Find matching definitions not yet unlocked
    const newAchievements = [];

    for (const def of ACHIEVEMENT_DEFINITIONS) {
      if (existingTypes.has(def.type)) continue;
      if (def.criteria.event !== eventType) continue;

      const value = eventData.count ?? eventData.streak ?? 0;
      if (value >= def.criteria.threshold) {
        // Award this achievement
        const achievementRef = doc(
          collection(db, USERS_COLLECTION, userId, ACHIEVEMENTS_SUBCOLLECTION)
        );

        const achievementData = {
          type: def.type,
          tier: def.tier,
          unlockedAt: serverTimestamp(),
          seen: false,
        };

        await setDoc(achievementRef, achievementData);

        newAchievements.push({
          id: achievementRef.id,
          ...achievementData,
          ...def,
          unlockedAt: new Date().toISOString(),
        });
      }
    }

    return newAchievements;
  } catch (error) {
    // Non-critical: don't block the user's action if achievement check fails
    if (process.env.NODE_ENV === 'development') {
      console.error('[Check achievements]', error);
    }
    return [];
  }
};

/**
 * Mark an achievement as seen by the user.
 * @param {string} userId - The user's ID.
 * @param {string} achievementId - The achievement document ID.
 * @returns {Promise<void>}
 */
export const markAchievementSeen = async (userId, achievementId) => {
  try {
    const achievementRef = doc(
      db,
      USERS_COLLECTION,
      userId,
      ACHIEVEMENTS_SUBCOLLECTION,
      achievementId
    );
    await updateDoc(achievementRef, { seen: true });
  } catch (error) {
    handleServiceError(error, 'Mark achievement seen', 'Failed to update achievement. Please try again.');
  }
};

/**
 * Get unseen achievements for a user (for popup notifications).
 * @param {string} userId - The user's ID.
 * @returns {Promise<Array>} Array of unseen achievement objects with definitions.
 */
export const getUnseenAchievements = async (userId) => {
  try {
    const achievementsRef = collection(
      db,
      USERS_COLLECTION,
      userId,
      ACHIEVEMENTS_SUBCOLLECTION
    );
    const q = query(achievementsRef, where('seen', '==', false));
    const querySnapshot = await getDocs(q);
    const unseen = querySnapshot.docs.map(serializeAchievement);

    // Enrich with definition data
    return unseen.map((a) => {
      const def = ACHIEVEMENT_DEFINITIONS.find((d) => d.type === a.type);
      return { ...a, ...(def || {}) };
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Get unseen achievements]', error);
    }
    return [];
  }
};
